import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PublicNav, PublicFooter } from "@/components/PublicShell";
import { Star, MapPin, ArrowUpRight, Loader2 } from "lucide-react";

export default function FeaturedJobs() {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    api.get("/jobs/published").then(({ data }) => setJobs(data)).catch(() => setJobs([]));
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F9FA] text-[#09090B]">
      <PublicNav />
      <section className="pt-16">
        <div className="max-w-[1400px] mx-auto px-6 py-16">
          <p className="overline mb-3">Opportunities</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Featured & open roles</h1>
          <p className="mt-4 text-muted-foreground max-w-xl">Curated leadership opportunities across the innovation economy. Sign in as a candidate to signal interest.</p>

          {!jobs && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
          {jobs && jobs.length === 0 && (
            <div className="mt-12 border border-black/10 bg-white p-12 lg:p-16 text-center" data-testid="jobs-empty-state">
              <div className="h-12 w-12 mx-auto bg-primary/10 flex items-center justify-center">
                <Star className="h-6 w-6 text-primary" strokeWidth={1.5} />
              </div>
              <p className="font-heading font-bold text-xl mt-6">New roles are on the way</p>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                We publish curated leadership opportunities as our searches go live. Create a candidate
                profile to be first in line — or, if you're hiring, post a role to reach vetted talent.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <Link to="/for-candidates" data-testid="empty-candidate-cta" className="px-6 py-2.5 text-sm font-medium border border-black/15 hover:bg-black/5 transition-colors">
                  Create a candidate profile
                </Link>
                <Link to="/for-clients" data-testid="empty-client-cta" className="px-6 py-2.5 text-sm font-medium bg-primary text-white hover:bg-[#09090B] transition-colors">
                  Post a role
                </Link>
              </div>
            </div>
          )}
          {jobs && jobs.length > 0 && (
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
              {jobs.map((j) => (
                <div key={j.id} className="group bg-white p-6 hover:bg-[#F1F1F3] transition-colors">
                  <div className="flex items-start justify-between">
                    <span className="text-[0.6rem] font-mono uppercase tracking-wider text-muted-foreground">{j.role_category}</span>
                    {j.featured && <span className="flex items-center gap-1 text-[0.6rem] font-mono text-primary"><Star className="h-3 w-3 fill-primary" /> FEATURED</span>}
                  </div>
                  <h3 className="font-heading font-bold text-lg mt-3">{j.title}</h3>
                  <p className="text-sm text-muted-foreground">{j.organization_name}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                    {j.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {j.location}</span>}
                    {j.work_arrangement && <span className="capitalize">{j.work_arrangement}</span>}
                    {j.employment_type && <span className="capitalize">{(j.employment_type || "").replace(/_/g, " ")}</span>}
                  </div>
                  <Link to="/for-candidates" className="mt-4 inline-flex items-center gap-1 text-sm text-primary group-hover:gap-2 transition-all">
                    Signal interest <ArrowUpRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
